/**
 * VariablesEditor
 *
 * Key-value editor for CallFlow prompt variables.
 * Shows auto-filled lead fields as read-only, and lets users add/remove
 * their own static variables (e.g. {job_title}, {company_name}).
 */

import { useState } from 'react';
import { Plus, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const LEAD_BUILTINS = [
  { key: 'first_name', description: 'Lead first name' },
  { key: 'last_name', description: 'Lead last name' },
  { key: 'full_name', description: 'Lead full name (first + last)' },
  { key: 'email', description: 'Lead email address' },
  { key: 'phone_number', description: 'Lead phone number' },
];

interface VariablesEditorProps {
  variables: Record<string, string>;
  onChange: (variables: Record<string, string>) => void;
  disabled?: boolean;
  /** Optional: variable names detected from assistant system prompt */
  detectedVariables?: string[];
}

export function VariablesEditor({
  variables,
  onChange,
  disabled = false,
  detectedVariables = [],
}: VariablesEditorProps) {
  const builtinKeys = LEAD_BUILTINS.map((b) => b.key);

  // Rows are the user-defined (non-builtin) variables
  const [rows, setRows] = useState<Array<{ key: string; value: string }>>(() =>
    Object.entries(variables)
      .filter(([k]) => !builtinKeys.includes(k))
      .map(([key, value]) => ({ key, value: String(value) }))
  );

  // Variables detected in the prompt that aren't builtins and not yet in rows
  const missingDetected = detectedVariables.filter(
    (v) => !builtinKeys.includes(v) && !rows.some((r) => r.key === v)
  );

  const syncUpstream = (updated: Array<{ key: string; value: string }>) => {
    const obj: Record<string, string> = {};
    for (const { key, value } of updated) {
      if (key.trim()) obj[key.trim()] = value;
    }
    onChange(obj);
  };

  const addRow = (key = '') => {
    const updated = [...rows, { key, value: '' }];
    setRows(updated);
    syncUpstream(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    syncUpstream(updated);
  };

  const updateRow = (index: number, field: 'key' | 'value', val: string) => {
    const updated = rows.map((r, i) => (i === index ? { ...r, [field]: val } : r));
    setRows(updated);
    syncUpstream(updated);
  };

  return (
    <div className="space-y-4">
      {/* Auto-filled builtins */}
      <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          Auto-filled from lead data — no configuration needed
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {LEAD_BUILTINS.map(({ key, description }) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">
                {'{'}
                {key}
                {'}'}
              </code>
              <span className="text-muted-foreground">{description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Missing detected variables hint */}
      {missingDetected.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
            Variables found in the assistant prompt that need values:
          </p>
          <div className="flex flex-wrap gap-2">
            {missingDetected.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => addRow(v)}
                disabled={disabled}
                className="flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-mono text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
              >
                <Plus className="h-3 w-3" />
                {'{'}
                {v}
                {'}'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* User-defined static variables */}
      <div className="space-y-2">
        {rows.length > 0 && (
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-muted-foreground px-1">
            <span>Variable name</span>
            <span>Value (same for all leads)</span>
            <span />
          </div>
        )}
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-mono select-none">
                {'{'}
              </span>
              <Input
                value={row.key}
                onChange={(e) => updateRow(index, 'key', e.target.value)}
                placeholder="variable_name"
                disabled={disabled}
                className="pl-5 pr-5 font-mono text-sm"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-mono select-none">
                {'}'}
              </span>
            </div>
            <Input
              value={row.value}
              onChange={(e) => updateRow(index, 'value', e.target.value)}
              placeholder="e.g. Software Engineer"
              disabled={disabled}
              className="text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeRow(index)}
              disabled={disabled}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addRow()}
          disabled={disabled}
          className="mt-1"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add variable
        </Button>
      </div>
    </div>
  );
}

/** Extract {variable_name} patterns from a string, returning unique names. */
export function detectVariables(text: string): string[] {
  const matches = text.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g) || [];
  return [...new Set(matches.map((m) => m.slice(1, -1)))];
}
